import { useFormik } from "formik";
import { Dispatch, SetStateAction } from "react";
import styled from "styled-components";
import * as yup from "yup";
import Button from "../../../../designComponents/Button";
import MediaUploader from "../../../../designComponents/MediaUpload/MediaUpload";
import Modal from "../../../../designComponents/Modal";
import SolidRadio from "../../../../designComponents/Radio/SolidRadio";
import Typography from "../../../../designComponents/Typography";
import { colors } from "../../../../theme/colors";
import { fontFamily } from "../../../../theme/fontFamily";
import { ReviewFieldNameType, ReviewReportSend } from "../types";
type Props = {
  fieldName: ReviewFieldNameType;
  visible: boolean;
  onClose: () => void;
  setReviewsData: Dispatch<SetStateAction<ReviewReportSend[]>>;
  reviewsData: ReviewReportSend[];
};

const makeModalTitle = (field: ReviewFieldNameType) => {
  switch (field) {
    case "Images":
      return "Property Images Review";
    case "Amenities":
      return "Amenities Review";
    case "Inventory":
      return "Inventory Review";
    case "Policy":
      return "Policy Review";
    case "RefRoom":
      return "Reference Images Review";
    case "Rules":
      return "Rules Review";

    default:
      return "Review";
  }
};

const reviewValidationSchema = yup.object().shape({
  status: yup
    .string()
    .required("Status is required")
    .oneOf(["accept", "reject"], "Invalid status"),
  description: yup.string().when("status", {
    is: "reject",
    then: (schema) => schema.required("Reason is required when rejecting"),
    otherwise: (schema) => schema.notRequired(),
  }),
  filesFile: yup.array().of(
    yup.object().shape({
      file: yup.mixed(),
      preview: yup.string(),
    })
  ),
  files: yup.array().of(yup.string()),
});
const CreateReview = ({
  visible,
  fieldName,
  onClose,
  setReviewsData,
  reviewsData,
}: Props) => {
  const reviewValueIndex = reviewsData.findIndex((x) => x.field === fieldName);
  const reviewValue =
    reviewValueIndex >= 0 ? reviewsData[reviewValueIndex] : undefined;

  const { values, setFieldValue, errors, handleChange, handleSubmit, touched } =
    useFormik<ReviewReportSend>({
      initialValues: {
        description: reviewValue?.description ?? "",
        field: fieldName || "Images",
        status: reviewValue?.status || "",
        filesFile: reviewValue?.filesFile ?? [],
        files: reviewValue?.files ?? [],
      },
      enableReinitialize: true,
      validationSchema: reviewValidationSchema,
      onSubmit: (values) => {
        if (reviewValue) {
          reviewsData[reviewValueIndex] = values;
          setReviewsData(reviewsData);
        } else {
          reviewsData.push(values);
          setReviewsData(reviewsData);
        }
        onClose();
      },
    });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files?.length > 0) {
      const files = Array.from(e.target.files);
      const newPreviews = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      const prevFiles = values?.filesFile || [];

      setFieldValue("filesFile", [...prevFiles, ...newPreviews]);
    }
  };
  const handleImageRemove = (index: number) => {
    const filteredFiles = (values?.filesFile || []).filter(
      (_, i) => i !== index
    );

    setFieldValue("filesFile", filteredFiles);
  };
  return (
    <Modal
      title={makeModalTitle(fieldName)}
      halfWidth
      isOpen={visible}
      onClose={() => {
        onClose();
      }}
    >
      <form onSubmit={handleSubmit}>
        <FormWrapperStyled>
          <Typography size={"largeTitle"} fontWeight="medium">
            Review status
          </Typography>
          <Typography size={"regular"} color="dark.neutral_1b">
            Do you accept or reject content of{" "}
            {makeModalTitle(fieldName)?.toLowerCase()}.
          </Typography>
          <ReviewStatusWrapperStyled>
            <SolidRadio
              selected={values.status}
              style={{}}
              name="status"
              onChange={(optionValue) => {
                setFieldValue("status", optionValue);
              }}
              options={[
                {
                  label: "Accept",
                  value: "accept",
                  acceptBgColor: colors.status.success,
                  className: "accept",
                },
                {
                  label: "Reject",
                  value: "reject",
                  className: "reject",
                  acceptBgColor: colors.status.danger,
                },
              ]}
            />
          </ReviewStatusWrapperStyled>
          {touched?.status && errors?.status && (
            <ErrorMessageStyled>{errors?.status}</ErrorMessageStyled>
          )}
          {values.status === "reject" && (
            <div>
              <Typography size={"largeTitle"} fontWeight="medium">
                Reason
              </Typography>
              <textarea
                value={values?.description}
                style={{
                  backgroundColor: "#F9F9FD",
                  margin: "14px 0px",
                  width: "100%",
                  minHeight: "200px",
                  resize: "none",
                  padding: "10px",
                  fontSize: "1rem",
                  borderRadius: "4px",
                  border: "1px solid #F1EFFB",
                  fontFamily: fontFamily.poppins,
                }}
                placeholder="Provide a valid reason for rejection..."
                name="description"
                onChange={handleChange}
              />
              {touched?.description && errors?.description && (
                <ErrorMessageStyled>{errors?.description}</ErrorMessageStyled>
              )}
              <MediaUploader
                multiple
                onChange={handleImageChange}
                image={values?.filesFile?.map((x) => x.preview) ?? []}
                acceptedFileTypes={[".png", ".jpg", ".jpeg", ".pdf"]}
                onRemove={handleImageRemove}
              />
            </div>
          )}
        </FormWrapperStyled>
        <div
          style={{
            borderTop: "1px solid #F1EFFB",
            paddingTop: 20,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button radius="normal" type="submit">
            Collect Review
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateReview;

const ReviewStatusWrapperStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 50px;
  margin: 10px 0px 30px;

  & .radio-label-wrapper {
    height: 38px;
    width: 160px;

    & .radio-label {
      user-select: none;
      border-radius: 12px;
      height: 38px;
      width: 160px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.5);
      font-size: 1.1rem;

      &:active {
        box-shadow: 1px 1px 0px 0px rgba(0, 0, 0, 0.5);
      }
    }

    &.accept .radio-label {
      box-shadow: 1px 1px 2px 1px green;
    }
    &.reject .radio-label {
      box-shadow: 1px 1px 2px 1px red;
    }
  }
`;

const FormWrapperStyled = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;
`;
const ErrorMessageStyled = styled.p`
  font-weight: 400;
  font-size: 0.9rem;
  color: red;
  margin-bottom: 20px;
`;
